/* ============================================================
   US-Parts24 · R572 — Diagnose-Bühne der Anfrageseite.

   Zwei Betriebsarten:
   1) SZENEN (solange die Kapitel laufen): Scrollstand → Szene 1–4 →
      Variablen am Gerät (--udx-n, --udx-fz, --udx-zeilen, --udx-angebot)
      und der Stand der Prüfliste. Rückwärts scrollen spult zurück.
   2) LIVE (sobald das Formular erreicht ist, nur am Rechner sichtbar):
      Das Gerät zeigt die Eingabe des Kunden — VIN in den Zellen, das
      entschlüsselte Fahrzeug aus den versteckten Detailfeldern, die
      decodeVin (usparts.js) füllt, Teil, Kontakt, nach dem Absenden den
      Eingang. usparts.js und die R567-Stufen werden nur GELESEN.

   Messung nur über getBoundingClientRect (Scroller ab 990 px ist
   .page-wrapper, darunter das Fenster); Scroll-Ereignisse in der
   Capture-Phase am document. Stabile Höhe über 100svh (Safari-Leiste).
   ============================================================ */
(() => {
  'use strict';
  const klemmen = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
  const schmal = window.matchMedia('(max-width: 989px)');
  /* Handy quer: Gerät links, Text rechts wie am Rechner — Lesezeile dann in der Bildmitte (Runde 4) */
  const quer = window.matchMedia('(max-width: 989px) and (orientation: landscape) and (max-height: 520px)');
  const VIN_OK = /^[A-HJ-NPR-Z0-9]{17}$/;
  const norm = (v) => (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/I/g, '1').replace(/[OQ]/g, '0');

  function starten(root) {
    if (!root || root.dataset.udxLaeuft) return;
    root.dataset.udxLaeuft = '1';

    const buehne = root.querySelector('[data-udx-buehne]');
    const kapitel = [...root.querySelectorAll('[data-udx-k]')];
    const formular = root.querySelector('[data-udx-formular]');
    /* Kurze Variante (Startseite): kein Kapitel-Text, der Scroll durch den
       ganzen Abschnitt spielt die vier Szenen; Schritte links leuchten mit. */
    const kurz = root.querySelector('.udx-kurz');
    const schritte = [...root.querySelectorAll('[data-udx-schritt]')];
    if (!buehne || (!kapitel.length && !kurz)) return;

    const zellen = [...buehne.querySelectorAll('.udx-zelle')];
    const beispiel = (buehne.querySelector('[data-udx-zellen]') || {}).dataset?.beispiel || '';
    const zaehler = buehne.querySelector('[data-udx-zaehler]');
    const vinTitel = buehne.querySelector('[data-udx-vin-titel]');
    const status = buehne.querySelector('[data-udx-status]');
    const name = buehne.querySelector('[data-udx-name]');
    const pz = buehne.querySelector('[data-udx-pz]');
    const angebotFz = buehne.querySelector('[data-udx-angebot-fz]');
    const felder = [...buehne.querySelectorAll('[data-udx-feld]')];
    const pruef = [...buehne.querySelectorAll('[data-udx-pruef]')];
    const pruefText = [...buehne.querySelectorAll('[data-udx-pruef-text]')];
    const lauf = [...buehne.querySelectorAll('.udx-lauf li')];
    function laufSetzen(fertig) {
      const jetzt = fertig.indexOf(false);
      lauf.forEach((li, i) => { li.classList.toggle('is-fertig', Boolean(fertig[i])); li.classList.toggle('is-jetzt', i === jetzt); });
    }

    /* Beispiel-Inhalte merken, damit der Rückweg aus LIVE sie wiederherstellt */
    const bsp = {
      name: name ? name.textContent : '',
      felder: felder.map((f) => f.textContent),
      pruef: pruefText.map((p) => p.textContent),
      angebotFz: angebotFz ? angebotFz.textContent : '',
    };

    const form = formular && formular.querySelector('form');
    const f = (sel) => (form ? form.querySelector(sel) : null);
    const eing = {
      vin: f('[data-usp-vin]'),
      teil: f('[data-dx-teil]') || f('[name="contact[Gesuchtes Teil]"]'),
      tnr: f('[data-dx-tnr]'),
      mail: f('[name="contact[email]"]'),
      detail: (k) => f(`[data-usp-detail="${k}"]`),
      manuell: form ? [...form.querySelectorAll('[data-dx-manuell]')] : [],
    };

    const sonde = document.createElement('div');
    sonde.style.cssText = 'position:absolute;left:0;top:0;width:1px;height:100vh;height:100svh;visibility:hidden;pointer-events:none;';
    root.appendChild(sonde);
    let svh = sonde.offsetHeight || window.innerHeight;
    let breite = window.innerWidth;

    let szene = 0;
    let live = null;
    let sichtbar = true;
    let geplant = false;

    function setzen(el, k, v) { if (el && el.style.getPropertyValue(k) !== v) el.style.setProperty(k, v); }
    function text(el, t) { if (el && el.textContent !== t) el.textContent = t; }
    const schirm = buehne.querySelector('.udx-schirm');
    function scan() {
      buehne.classList.remove('is-scan');
      void buehne.offsetWidth;
      /* Runde 2 Tempo: Strecke der Abtastlinie (CSS @keyframes udx-scan läuft per transform, nicht mehr per top).
         Nur messen, wenn das Gerät im Bild ist — sonst kostet das Messen beim Laden Rechenzeit für nichts. */
      if (schirm && ioGemeldet && sichtbar) buehne.style.setProperty('--udx-scan-weg', (schirm.offsetHeight + 4) + 'px');
      buehne.classList.add('is-scan');
    }

    /* Runde 2 Tempo (01.10.2026, CLS beim Scrollen): Am Handy bricht „US-Parts24 · Diagnose“ in eine zweite Zeile
       um, sobald die Statuszeile lang ist („Angebot wird erstellt …“). Der Gerätekopf wechselte dadurch je Szene
       28 ↔ 39 px, Schirm, LED und Status sprangen mit. Jetzt hält der Kopf die Höhe seiner längsten Statuszeile:
       gemessen mit der längsten Statuszeile (Schrift ist JetBrains Mono, also gleich breit je Zeichen: längster
       Text = breitester), EIN Messgang, erst wenn das Gerät in die Nähe kommt und die Schriften da sind; neu bei
       neuer Breite. Am Rechner bricht nichts um → dort wird nicht gemessen und nichts gesetzt.
       (Erste Fassung maß alle 11 Texte nacheinander schon beim Laden: Blockierzeit gedrosselt +0,4–1 s.) */
    const kopf = buehne.querySelector('.udx-geraet__kopf');
    const STATUS_TEXTE = ['Lese VIN …', 'VIN eingelesen', 'Fahrzeug erkannt', 'Prüfung läuft …', 'Prüfung fertig',
      'Angebot wird erstellt …', 'Festpreis erstellt', 'Anfrage eingegangen', 'Warte auf VIN …', 'Teil erfasst', 'Bereit zum Senden'];
    const LAENGSTER = STATUS_TEXTE.reduce((a, t) => (t.length > a.length ? t : a), '');
    let kopfFuer = 0;
    let ioGemeldet = false;
    function kopfHalten() {
      if (!kopf || !status) return;
      if (!schmal.matches) { kopf.style.minHeight = ''; return; }
      const jetzt = status.textContent;
      kopf.style.minHeight = '';
      if (jetzt.length < LAENGSTER.length) status.textContent = LAENGSTER;
      const h = kopf.getBoundingClientRect().height;
      if (status.textContent !== jetzt) status.textContent = jetzt;
      if (h > 0) kopf.style.minHeight = h + 'px';
    }
    function kopfHaltenBald() {
      if (kopfFuer === breite) return;
      kopfFuer = breite;
      const los = () => requestAnimationFrame(kopfHalten);
      if (document.fonts && document.fonts.status !== 'loaded' && document.fonts.ready) document.fonts.ready.then(los);
      else los();
    }
    /* Rechner, Anfrageseite: Im LIVE-Teil (eigene Eingabe) ist der Schirm ~30 px niedriger als in Szene 4; das
       mittig stehende Gerät rutschte nach. Beim Wechsel nach LIVE behält der Schirm die Höhe von eben. */
    function schirmHalten(an) {
      if (!schirm) return;
      schirm.style.minHeight = an && !schmal.matches ? schirm.getBoundingClientRect().height + 'px' : '';
    }
    function zellenText(t) {
      zellen.forEach((z, i) => text(z, t[i] || ''));
    }
    function pruefStand(werte) {
      pruef.forEach((li, i) => { const s = werte[i] || 'warte'; if (li.dataset.stand !== s) li.dataset.stand = s; });
    }

    /* ---------- 1) Szenen ---------- */
    function szenenZeigen(k, p) {
      if (live !== false) {
        if (live === true) schirmHalten(false);
        live = false;
        buehne.dataset.live = 'aus';
        buehne.classList.remove('is-eingang', 'is-warte');
        zellenText(beispiel);
        text(name, bsp.name);
        felder.forEach((el, i) => text(el, bsp.felder[i]));
        pruefText.forEach((el, i) => text(el, bsp.pruef[i]));
        text(angebotFz, bsp.angebotFz);
        text(pz, 'Prüfziffer ✓');
        pruef.forEach((li, i) => { const em = li.querySelector('em'); if (em) text(em, i === 2 ? 'kalkuliert' : 'geprüft'); });
      }
      if (k !== szene) { szene = k; buehne.dataset.szene = String(k); scan(); }

      let n = 17, fz = 1, zeilen = 5, pr = 3, angebot = 0, stempel = 0;
      if (k === 1) { n = Math.round(klemmen(p / 0.8) * 17); fz = 0; zeilen = 0; pr = 0; }
      else if (k === 2) { fz = klemmen(p * 4); zeilen = klemmen(p * 1.35) * 5; pr = 0; }
      else if (k === 3) { pr = klemmen(p * 1.25) * 3; }
      else if (k === 4) { angebot = klemmen(p * 2.2); stempel = p > 0.55 ? 1 : 0; }

      setzen(buehne, '--udx-n', String(n));
      setzen(buehne, '--udx-fz', fz.toFixed(3));
      setzen(buehne, '--udx-zeilen', zeilen.toFixed(3));
      setzen(buehne, '--udx-angebot', angebot.toFixed(3));
      setzen(buehne, '--udx-stempel', String(stempel));
      buehne.classList.toggle('is-vin-voll', n === 17);
      text(zaehler, n + '/17');
      text(vinTitel, n === 17 ? 'VIN eingelesen' : 'VIN wird eingelesen …');
      pruefStand([0, 1, 2].map((i) => (pr - i >= 1 ? 'ok' : pr - i > 0 ? 'laeuft' : 'warte')));
      text(status, ['', n === 17 ? 'VIN eingelesen' : 'Lese VIN …', 'Fahrzeug erkannt', pr >= 3 ? 'Prüfung fertig' : 'Prüfung läuft …', angebot >= 1 ? 'Festpreis erstellt' : 'Angebot wird erstellt …'][k]);
      laufSetzen([0, 1, 2, 3].map((i) => i < k - 1 || (i === k - 1 && (k === 1 ? n === 17 : k === 3 ? pr >= 3 : k === 4 ? angebot >= 1 : fz >= 1 && zeilen >= 5))));
    }

    /* ---------- 2) Live: das Gerät zeigt die eigene Eingabe ---------- */
    function liveZeigen() {
      if (live !== true) { schirmHalten(true); live = true; buehne.dataset.live = 'an'; szene = 0; buehne.dataset.szene = '4'; scan(); }
      const vin = eing.vin ? norm(eing.vin.value).slice(0, 17) : '';
      const n = vin.length;
      zellenText(vin);
      setzen(buehne, '--udx-n', String(n));
      buehne.classList.toggle('is-vin-voll', n === 17 && VIN_OK.test(vin));
      text(zaehler, n + '/17');
      text(vinTitel, n ? (n === 17 ? 'Deine VIN' : 'Deine VIN …') : 'Warte auf deine VIN');

      const fzName = eing.detail('vehicle') ? eing.detail('vehicle').value : '';
      const manuell = eing.manuell.map((m) => m.value.trim()).filter(Boolean).join(' ');
      const hatFz = Boolean(fzName || manuell);
      setzen(buehne, '--udx-fz', hatFz ? '1' : '0');
      text(name, fzName || manuell || '');
      /* Die Fahrzeugkarte im Formular zeigt die Werte schon deutsch und kurz
         („4×4", „370 PS"); die versteckten Felder tragen Rohwerte der NHTSA
         („4WD/4-Wheel Drive/4x4", „DEARBORN, MICHIGAN, UNITED STATES (USA)"). */
      const karte = {};
      if (form) form.querySelectorAll('.usp-vehcard__row').forEach((r) => {
        const k = (r.querySelector('.usp-vehcard__k') || {}).textContent || '';
        const v = (r.querySelector('.usp-vehcard__v') || {}).textContent || '';
        if (k) karte[k.trim().toLowerCase()] = v.trim();
      });
      const schoen = (v) => v.split(',').slice(0, 2).map((x) => x.trim().toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase())).join(', ');
      const wert = {
        bodyclass: karte.aufbau,
        engine: karte.motor,
        hp: karte.leistung,
        drive: karte.antrieb,
        plant: karte.werk || (eing.detail('plant') && eing.detail('plant').value ? schoen(eing.detail('plant').value) : ''),
      };
      felder.forEach((el) => text(el, wert[el.dataset.udxFeld] || '—'));
      setzen(buehne, '--udx-zeilen', fzName ? '5' : '0');
      const pzWert = eing.detail('pruefziffer') ? eing.detail('pruefziffer').value : '';
      text(pz, pzWert === 'stimmt' ? 'Prüfziffer ✓' : pzWert ? 'Prüfziffer prüfen' : '');

      const teil = eing.teil ? eing.teil.value.trim() : '';
      const tnr = eing.tnr ? eing.tnr.value.trim() : '';
      /* NUR ablesen: checkValidity() feuert bei leerem Pflichtfeld ein
         invalid-Ereignis — darauf öffnet R567 Stufe 03 und springt ins
         E-Mail-Feld. Gemessen: VIN-Eingabe landete zur Hälfte in der E-Mail. */
      const mailOk = eing.mail ? eing.mail.validity.valid && eing.mail.value.includes('@') : false;
      text(pruefText[0], hatFz ? 'Fahrzeug: ' + (fzName || manuell) : 'Fahrzeug');
      text(pruefText[1], teil ? 'Teil: ' + teil : tnr ? 'Teilenummer: ' + tnr : 'Teil');
      text(pruefText[2], 'Kontakt');
      pruef.forEach((li) => { const em = li.querySelector('em'); if (em) text(em, 'erfasst'); });
      pruefStand([hatFz ? 'ok' : n ? 'laeuft' : 'warte', teil || tnr ? 'ok' : hatFz ? 'laeuft' : 'warte', mailOk ? 'ok' : teil || tnr ? 'laeuft' : 'warte']);
      setzen(buehne, '--udx-angebot', '0');
      setzen(buehne, '--udx-stempel', '0');

      const eingegangen = Boolean(form && (form.classList.contains('is-done') || form.querySelector('.usp-success')));
      buehne.classList.toggle('is-eingang', eingegangen);
      const warte = !hatFz;
      buehne.classList.toggle('is-warte', warte && !eingegangen);
      text(status, eingegangen ? 'Anfrage eingegangen'
        : !n && !hatFz ? 'Warte auf VIN …'
        : !hatFz ? 'Lese VIN …'
        : !(teil || tnr) ? 'Fahrzeug erkannt'
        : !mailOk ? 'Teil erfasst'
        : 'Bereit zum Senden');
      laufSetzen([n === 17 || hatFz, hatFz, Boolean(teil || tnr) && mailOk, eingegangen]);
    }

    /* ---------- Takt ---------- */
    function leselinie() {
      if (schmal.matches && !quer.matches) {
        const b = buehne.getBoundingClientRect();
        const unten = Math.max(0, Math.min(b.bottom, svh));
        return unten + (svh - unten) * 0.35;
      }
      return svh * 0.5;
    }

    let aktiv = -1;
    let formFokus = false;
    if (form) {
      form.addEventListener('focusin', () => { formFokus = true; anfordern(); });
      form.addEventListener('focusout', () => setTimeout(() => { formFokus = form.contains(document.activeElement); anfordern(); }, 0));
    }
    function taktKurz() {
      /* Die Szenen beginnen schon beim Hereinscrollen (Oberkante bei 60 %),
         sonst steht das Gerät beim Erscheinen leer da. */
      const r = kurz.getBoundingClientRect();
      const start = svh * 0.6;
      const weg = Math.max(1, r.height - svh + start);
      const p = klemmen((start - r.top) / weg);
      const k = Math.min(4, Math.floor(p * 4) + 1);
      const lokal = klemmen(p * 4 - (k - 1));
      schritte.forEach((li, i) => {
        li.classList.toggle('is-jetzt', i === k - 1);
        li.classList.toggle('is-fertig', i < k - 1 || (i === 3 && p >= 0.98));
      });
      szenenZeigen(k, k === 4 ? klemmen(lokal * 1.3) : lokal);
    }

    function takt() {
      geplant = false;
      if (kurz) { taktKurz(); return; }
      const linie = leselinie();
      const formOben = formular ? formular.getBoundingClientRect().top : Infinity;
      /* Live ab Bildmitte — oder sofort, sobald jemand ins Formular klickt.
         Früher (ab 80 %) verdrängte das Formular das Finale von Szene 4. */
      if (!schmal.matches && (formOben <= linie || formFokus)) {
        if (aktiv !== -2) { kapitel.forEach((k) => k.classList.remove('is-aktiv')); aktiv = -2; }
        liveZeigen();
        return;
      }
      let k = 0;
      for (let i = 0; i < kapitel.length; i++) if (kapitel[i].getBoundingClientRect().top <= linie) k = i;
      const r = kapitel[k].getBoundingClientRect();
      const p = klemmen((linie - r.top) / Math.max(1, r.height));
      if (k !== aktiv) { kapitel.forEach((el, i) => el.classList.toggle('is-aktiv', i === k)); aktiv = k; }
      szenenZeigen(k + 1, p);
    }
    function anfordern() {
      if (!sichtbar || geplant) return;
      geplant = true;
      requestAnimationFrame(() => geplant && takt());
      setTimeout(() => geplant && takt(), 120);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((e) => { ioGemeldet = true; sichtbar = e.some((x) => x.isIntersecting); if (sichtbar) { kopfHaltenBald(); anfordern(); } }, { rootMargin: '300px 0px' }).observe(root);
    }
    document.addEventListener('scroll', anfordern, { passive: true, capture: true });
    window.addEventListener('resize', () => {
      if (window.innerWidth !== breite) { breite = window.innerWidth; svh = sonde.offsetHeight || window.innerHeight; if (live) schirmHalten(false); if (sichtbar) kopfHaltenBald(); }
      anfordern();
    });
    if (form) {
      form.addEventListener('input', anfordern);
      form.addEventListener('change', anfordern);
      /* decodeVin füllt die Detailfelder asynchron und ohne input-Ereignis */
      const karte = form.querySelector('[data-usp-vin-card]');
      if (karte && 'MutationObserver' in window) new MutationObserver(anfordern).observe(karte, { attributes: true, childList: true });
      new MutationObserver(anfordern).observe(form, { attributes: true, attributeFilter: ['class'] });
    }
    /* „Diagnose starten": zum VIN-Feld im Hero und den Cursor hineinsetzen */
    root.querySelectorAll('[data-udx-start]').forEach((a) => a.addEventListener('click', (e) => {
      const feld = document.querySelector('.usp-hero__box [data-usp-vin]');
      if (!feld) return; // ohne Hero: normaler Sprunglink
      e.preventDefault();
      /* Messbar machen, ohne neues Feld (Leitung 25.09.): die vorhandene
         Quelle des Hero-Formulars bekommt einen Zusatz. */
      const quelle = feld.form && feld.form.querySelector('[name="contact[Quelle]"]');
      const zusatz = ' · über Diagnose-Szene';
      if (quelle && !quelle.value.includes(zusatz)) quelle.value += zusatz;
      const box = feld.closest('.usp-hero__box') || feld;
      box.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      setTimeout(() => { try { feld.focus({ preventScroll: true }); } catch (_) { feld.focus(); } }, 650);
    }));

    root.classList.add('is-bereit');
    takt();
  }

  function alle() { document.querySelectorAll('[data-udx]').forEach(starten); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', alle, { once: true });
  else alle();
  document.addEventListener('shopify:section:load', alle);
})();
